// PATH: src/components/chapters/ChapterFilterBar.tsx
import * as React from "react";
import {
  Box,
  Button,
  InputAdornment,
  ListSubheader,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import type { CurriculumProgram } from "../../models/domain";
import {
  COMMON_TRACK,
  findGrade,
  findProgram,
  gradeGroups,
  gradeTracks,
  subjectOptions,
  type ChapterFilters,
  type ChapterState,
} from "./chapterFilters";
import { CHAPTER_ISSUES, ISSUE_FILTER_LABELS, type IssueFilter } from "../coverage/chapterIssues";
import { REVIEW_FILTER_LABELS, REVIEW_STATUSES, type ReviewFilter } from "./reviewStatus";

interface Props {
  programs: CurriculumProgram[];
  filters: ChapterFilters;
  onChange: (next: Partial<ChapterFilters>) => void;
  disabled?: boolean;
  /**
   * Défauts que le serveur mesure (leçons, objectifs officiels) ; null =
   * complétude pas chargée : le filtre reste choisissable (lien partagé).
   */
  measured?: { lessons: boolean } | null;
}

const fieldSx = { minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "0 1 220px" } };

/**
 * Barre de filtres : programme → niveau → matière → filière, recherche, état.
 * Chaque choix réinitialise ce qui en dépend (fait par `normalizeFilters`).
 */
export const ChapterFilterBar: React.FC<Props> = ({ programs, filters, onChange, disabled, measured }) => {
  const program = findProgram(programs, filters.program);
  const groups = gradeGroups(program);
  const entry = findGrade(program, filters.grade);
  const tracks = gradeTracks(entry);
  const subjects = subjectOptions(program, entry, filters.track);

  // Recherche : saisie locale, URL mise à jour après une courte pause.
  const [q, setQ] = React.useState(filters.q);
  React.useEffect(() => setQ(filters.q), [filters.q]);
  React.useEffect(() => {
    if (q === filters.q) return;
    const t = window.setTimeout(() => onChange({ q }), 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const gradeItems: React.ReactNode[] = [<MenuItem key="" value="">Tous les niveaux</MenuItem>];
  groups.forEach((g) => {
    gradeItems.push(<ListSubheader key={`h-${g.stage.id}`}>{g.stage.name}</ListSubheader>);
    g.grades.forEach((gr) =>
      gradeItems.push(
        <MenuItem key={gr.grade.id} value={gr.grade.id} sx={{ pl: 3 }}>
          {gr.grade.name}
        </MenuItem>,
      ),
    );
  });

  const hasFilters = Boolean(
    filters.grade ||
      filters.subject ||
      filters.track ||
      filters.q ||
      filters.state !== "all" ||
      filters.issue !== "all" ||
      filters.review !== "all",
  );
  const notMeasured = (i: IssueFilter) =>
    Boolean(measured) && (i === "lessons_empty" || i === "lessons_weak") && !measured?.lessons;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
      {programs.length > 1 && (
        <TextField
          select
          label="Programme"
          value={filters.program}
          onChange={(e) => onChange({ program: e.target.value, grade: "", subject: "", track: "" })}
          disabled={disabled}
          sx={fieldSx}
        >
          {programs.map((p) => (
            <MenuItem key={p.program.id} value={p.program.id}>
              {p.program.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        label="Niveau"
        value={filters.grade}
        onChange={(e) => onChange({ grade: e.target.value })}
        disabled={disabled || !program}
        sx={fieldSx}
        SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 420 } } } }}
        InputLabelProps={{ shrink: true }}
      >
        {gradeItems}
      </TextField>

      <TextField
        select
        label="Matière"
        value={filters.subject}
        onChange={(e) => onChange({ subject: e.target.value })}
        disabled={disabled || !program}
        sx={fieldSx}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="">Toutes les matières</MenuItem>
        {subjects.map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>

      {tracks.length > 0 && (
        <TextField
          select
          label="Filière"
          value={filters.track}
          onChange={(e) => onChange({ track: e.target.value })}
          disabled={disabled}
          sx={fieldSx}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Toutes les filières</MenuItem>
          <MenuItem value={COMMON_TRACK}>Tronc commun seulement</MenuItem>
          {tracks.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
              {t.is_active ? "" : " (inactive)"}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        placeholder="Titre ou référence"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ ...fieldSx, flex: { xs: "1 1 100%", sm: "1 1 220px" } }}
        inputProps={{ "aria-label": "Rechercher un chapitre", dir: "auto" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        select
        label="Complétude"
        value={filters.issue}
        onChange={(e) => onChange({ issue: e.target.value as IssueFilter })}
        disabled={disabled}
        sx={fieldSx}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="all">Tous les chapitres</MenuItem>
        <MenuItem value="any">À compléter (au moins un défaut)</MenuItem>
        {CHAPTER_ISSUES.map((i) => (
          <MenuItem key={i} value={i} disabled={notMeasured(i)} sx={{ pl: 3 }}>
            {ISSUE_FILTER_LABELS[i]}
            {notMeasured(i) ? " (pas encore mesuré)" : ""}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Relecture"
        value={filters.review}
        onChange={(e) => onChange({ review: e.target.value as ReviewFilter })}
        disabled={disabled}
        sx={{ ...fieldSx, flex: { xs: "1 1 100%", sm: "0 1 190px" } }}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="all">{REVIEW_FILTER_LABELS.all}</MenuItem>
        {REVIEW_STATUSES.map((st) => (
          <MenuItem key={st} value={st} sx={{ pl: 3 }}>
            {REVIEW_FILTER_LABELS[st]}
          </MenuItem>
        ))}
      </TextField>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={filters.state}
        onChange={(_e, v: ChapterState | null) => v && onChange({ state: v })}
        aria-label="État"
        sx={{ bgcolor: "background.paper", "& .MuiToggleButton-root": { px: 1.5, textTransform: "none", py: 0.75 } }}
      >
        <ToggleButton value="all">Tous</ToggleButton>
        <ToggleButton value="active">Actifs</ToggleButton>
        <ToggleButton value="inactive">Inactifs</ToggleButton>
      </ToggleButtonGroup>

      {hasFilters && (
        <Button
          color="inherit"
          size="small"
          onClick={() => {
            setQ("");
            onChange({ grade: "", subject: "", track: "", q: "", state: "all", issue: "all", review: "all" });
          }}
          sx={{ color: "text.secondary" }}
        >
          Effacer les filtres
        </Button>
      )}
    </Box>
  );
};

export default ChapterFilterBar;
